import styled from 'styled-components';
import { Body1, Body5, Container, H5, Page } from '@ui';
import { FlatButton } from '@ui/buttons';
import React from 'react';
import { AI_TOOLS } from '@constants';
import BackArrowButton from '@ui/buttons/BackArrowButton';
import { SkeletonButton } from '@ui/buttons/SkeletonButton';
import { AdvancedSettingsButton } from './AdvancedSettings';
import { PROMPT_SERIES_MODES, usePromptSeries } from '@hooks/features/usePromptSeries';
import { Modal } from '@ui';
import { CloseButton } from '@ui/buttons/CloseButton';
import { DocumentSelection } from './DocumentSelection';
import { DocumentIcon } from '@assets/icons/svgs/DocumentIcon';
import { EvaluationTemplateResponseSummary } from './components/EvaluationTemplateResponseSummary';
import { useUserUploadedDocs } from '@hooks/features/useDocs';

const PageWrapper = styled.div`
  + div {
    width: 100%;
  }
`;

const Heading = styled.div`
  padding: 36px 0 0 0;

  h5 {
    padding-right: 8px;
  }
`;

const SelectedDocumentWrapper = styled.div``;
const IconWrapper = styled.div`
  margin-right: 8px;

  svg {
    padding-top: 1px;
    height: 16px;
    width: 16px;

    path {
      fill: ${({ theme }) => theme.themeColors.text};
    }
  }
`;
const CloseButtonWrapper = styled.div`
  margin-left: 8px;

  svg {
    padding-top: 1px;
    height: 12px;
    width: 12px;

    path {
      fill: ${({ theme }) => theme.themeColors.text};
    }
  }
`;
const ActionWrapper = styled.div`
  text-align: center;
  padding: 36px 0 0 0;
`;

function SelectedDocument({ doc, handleRemove }) {
  if (!doc) return null;

  const { filename, doc_type, report_date } = doc;
  return (
    <SelectedDocumentWrapper>
      <Container row verticallyCenter>
        <IconWrapper>
          <DocumentIcon />
        </IconWrapper>
        <Container>
          <Body5>{filename}</Body5>
        </Container>
        <CloseButtonWrapper>
          <CloseButton onClick={() => handleRemove(filename)} />
        </CloseButtonWrapper>
      </Container>
    </SelectedDocumentWrapper>
  );
}

const tool = AI_TOOLS.EVAL_TEMPLATE;

export default function EvalTemplateTool({ onGoBack }) {
  const [modalProps] = React.useState({ maximizeWidth: true, forceHeight: true });
  const [filters] = React.useState({ doc_types: 'PPM,Side Letter' });
  const { docs } = useUserUploadedDocs(filters);

  const [isSelectingDocs, setIsSelectingDocs] = React.useState(false);
  const [selectedDocs, setSelectedDocs] = React.useState([]);

  const { resultId, questionList, formError: error, onSubmit, onClear } = usePromptSeries(tool);

  const handleAddDocument = (v) => {
    setSelectedDocs((prevState) => [...prevState, v]);
  };

  const handleRemoveDocument = (filename) => {
    setSelectedDocs((prevState) => prevState.filter((sd) => sd.filename !== filename));
  };

  const onSelectDocumentsClick = () => setIsSelectingDocs(true);

  const onGenerateEvaluation = () => {
    if (selectedDocs.length === 0) return null;
    const docIds = selectedDocs.map((d) => d.id);
    onSubmit(null, PROMPT_SERIES_MODES.evaluationTool, docIds);
  };

  const handleClear = () => {
    onClear();
  };

  return (
    <PageWrapper>
      {isSelectingDocs && (
        <Modal onDismiss={() => setIsSelectingDocs(false)} modalProps={modalProps}>
          <Container row verticallyCenter spaceBetween>
            <Container bottom={4}>
              <Body1 bold>Select Document Sources</Body1>
            </Container>
            <CloseButton onClick={() => setIsSelectingDocs(false)} />
          </Container>
          <Container top={24}>
            <DocumentSelection
              docs={docs}
              filters={filters}
              onSelect={handleAddDocument}
              onRemoveSelect={handleRemoveDocument}
              selectedDocs={selectedDocs}
              onClose={() => setIsSelectingDocs(false)}
              noDocsMessage={
                'Uploaded documents with the document type "PPM" will show here. Only "PPM" documents are supported in this mode.'
              }
              singleDocMode
            />
          </Container>
        </Modal>
      )}
      <Page width={'1400px'}>
        <BackArrowButton onClick={onGoBack} />
        <Heading>
          <H5>Hedge Fund PPM/OM Evaluation Template</H5>
          <AdvancedSettingsButton tool={tool} />
        </Heading>
        <Container top={16}>
          <Body5>Please select one PPM and any related side letters or amendments.</Body5>
        </Container>
        <Container top={selectedDocs.length > 0 ? 40 : 24}>
          {selectedDocs.map((d, i) => (
            <Container key={d.id} top={0} bottom={8}>
              <SelectedDocument doc={d} handleRemove={handleRemoveDocument} />
            </Container>
          ))}
          <Container top={24}>
            {selectedDocs.length > 0 ? (
              <SkeletonButton onClick={onSelectDocumentsClick}>+ Select Documents</SkeletonButton>
            ) : (
              <FlatButton onClick={onSelectDocumentsClick}>+ Select Documents</FlatButton>
            )}
          </Container>
          {selectedDocs.length === 0 ? null : (
            <ActionWrapper>
              <FlatButton fullWidth onClick={onGenerateEvaluation}>
                Create Report
              </FlatButton>
            </ActionWrapper>
          )}
        </Container>
      </Page>
      {error && (
        <Page width={'1400px'}>
          <Container vertical={16}>
            <Container>
              <Body1 bold>An error occurred analyzing this document.</Body1>
            </Container>
            <Container top={16} bottom={48}>
              <Body5>{error}</Body5>
            </Container>
          </Container>
        </Page>
      )}
      {resultId && (
        <Page width={'1400px'}>
          <div id={'snapshot-answers-section'} />
          <EvaluationTemplateResponseSummary
            questionList={questionList}
            resultId={resultId}
            Heading={<H5>Report</H5>}
          />
          <Container top={24} centerAll>
            <SkeletonButton onClick={handleClear}>Close Section</SkeletonButton>
          </Container>
        </Page>
      )}
    </PageWrapper>
  );
}
